/**
 * R15 — Pagination automatique pour contourner le cap PostgREST par défaut (1000).
 *
 * PostgREST applique un cap implicite (configuré côté serveur, ~1000 par défaut)
 * sur toute requête `select` sans `.range()`. Pour les exports CSV, cela tronque
 * silencieusement les résultats au-delà de 1000 lignes (commentaire client R15 :
 * « Cela fonctionne mais nombre de donnée limité a 1000. Au dela l'export
 * fonctionne plus »).
 *
 * `fetchAllPages` boucle `.range(from, to)` par pages de `pageSize` jusqu'à ce
 * que la dernière page renvoie moins de `pageSize` éléments (signal d'épuisement)
 * ou jusqu'à atteindre le HARD_CAP (garde-fou).
 *
 * Usage :
 *   const buildQuery = () =>
 *     supabase
 *       .from('contacts_filter_view')
 *       .select('*')
 *       .order('created_at', { ascending: false });
 *
 *   const { data, error } = await fetchAllPages(buildQuery);
 *
 * NOTE : la factory est obligatoire car Supabase QueryBuilder est consommé après
 * un `await` ; on ne peut pas rejouer le même objet.
 */

const DEFAULT_PAGE_SIZE = 1000;
// Garde-fou absolu : empêche une boucle infinie si une query mal construite
// renvoie pageSize lignes en boucle. 200k = couverture confortable pour tout
// usage métier (CRM Desertours ~quelques milliers d'entités).
const HARD_CAP = 200_000;

export interface PaginatedResult<T> {
  data: T[] | null;
  error: any;
}

/**
 * Type de la factory : retourne un objet avec une méthode `range(from, to)`.
 * En pratique c'est un PostgrestFilterBuilder Supabase (chainable + Thenable),
 * mais on le type en `any` pour rester compatible avec les builders typés
 * par table/vue (Database['public']['Views'][...]) sans imposer de cast verbeux
 * dans chaque caller.
 */

type QueryFactory = () => any;

export async function fetchAllPages<T = any>(
  buildQuery: QueryFactory,
  pageSize: number = DEFAULT_PAGE_SIZE
): Promise<PaginatedResult<T>> {
  const all: T[] = [];
  let from = 0;

  while (from < HARD_CAP) {
    const to = from + pageSize - 1;
    const result = (await buildQuery().range(from, to)) as {
      data: T[] | null;
      error: any;
    };
    const { data, error } = result;

    if (error) {
      return { data: null, error };
    }

    if (!data || data.length === 0) {
      break;
    }

    all.push(...data);

    // Dernière page : signal d'épuisement
    if (data.length < pageSize) {
      break;
    }

    from += pageSize;
  }

  return { data: all, error: null };
}

/**
 * Chunke un tableau d'IDs et exécute `runner(chunk)` pour chaque batch en parallèle.
 *
 * Contournement de la limite PostgREST : un `WHERE id IN ($1, ..., $N)` génère
 * une URL `id=in.(uuid1,uuid2,...,uuidN)` qui peut dépasser la limite serveur
 * (~2-8 KB selon proxy/PostgREST). Au-delà, la requête échoue silencieusement
 * (response vide ou 414/500), masquant la cause.
 *
 * Cap empirique : 500 UUIDs/chunk = ~18 KB d'URL en safety zone.
 *
 * Usage :
 *   const rows = await fetchInChunks(ids, 500, async (chunk) => {
 *     const { data, error } = await supabase.from('table').select('*').in('id', chunk);
 *     if (error) throw error;
 *     return data || [];
 *   });
 *
 * Si `runner` jette pour un chunk, l'erreur est wrappée avec contexte
 * (`chunk N/M, size=K`) pour faciliter le debug.
 */
export async function fetchInChunks<T>(
  ids: string[],
  chunkSize: number,
  runner: (chunk: string[]) => Promise<T[]>
): Promise<T[]> {
  if (ids.length === 0) return [];
  if (ids.length <= chunkSize) {
    return runner(ids);
  }

  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += chunkSize) {
    chunks.push(ids.slice(i, i + chunkSize));
  }

  const total = chunks.length;
  try {
    const results = await Promise.all(
      chunks.map((chunk, index) =>
        runner(chunk).catch((err) => {
          const cause = err instanceof Error ? err.message : String(err);
          throw new Error(
            `Échec récupération chunk ${index + 1}/${total} (size=${chunk.length}) : ${cause}`
          );
        })
      )
    );
    return results.flat();
  } catch (err) {
    // Re-throw avec contexte global pour que l'appelant puisse afficher un toast clair
    if (err instanceof Error) throw err;
    throw new Error(`Échec récupération données (chunks ${total} × ${chunkSize}) : ${String(err)}`);
  }
}
